<template>
  <div class="list-container">
    <h2 class="list-title">Gestión de Personal</h2>

    <!-- Controles -->
    <div class="controls mb-4 d-flex justify-content-between align-items-center">
      <div class="filters d-flex gap-3">
        <input v-model="searchQuery" type="text" class="form-control" placeholder="Buscar por nombre o apellido" />
        <select v-model="filterRol" class="form-select">
          <option value="">Todos los roles</option>
          <option v-for="categoria in roles" :key="categoria.tipo" :value="categoria.tipo">
            {{ categoria.descripcion }}
          </option>
        </select>
        <select v-model="filterEstado" class="form-select">
          <option value="">Todos los estados</option>
          <option v-for="estado in estados" :key="estado.tipo" :value="estado.tipo">
            {{ estado.descripcion }}
          </option>
        </select>
      </div>
    </div>

    <!-- Tarjetas -->
    <div v-if="filteredPersonal.length > 0" class="row g-4">
      <div class="col-md-6 col-lg-4" v-for="persona in paginatedPersonal" :key="persona.id">
        <div class="card shadow-sm h-100">
          <div class="card-body">
            <h5 class="card-title mb-1">
              {{ persona.nombre }} {{ persona.apellido }}
            </h5>
            <h6 class="text-muted mb-2">Legajo: {{ persona.legajo }}</h6>
            <p class="mb-1"><strong>Rol:</strong> {{ persona.rol }}</p>
            <p class="mb-2">
              <strong>Estado:</strong>
              <span :class="estadoBadge(persona.estado)">
                {{ persona.estado }}
              </span>
            </p>
            <div class="d-flex gap-2">
              <button class="btn btn-primary btn-sm" @click="$emit('edit-personal', persona)" title="Editar">
                <font-awesome-icon :icon="['fas', 'edit']" />
              </button>
              <button class="btn btn-danger btn-sm" @click="$emit('delete-personal', persona.id)" title="Eliminar">
                <font-awesome-icon :icon="['fas', 'trash']" />
              </button>
              <button class="btn btn-info btn-sm" @click="viewPersonalDetails(persona)" title="Ver Detalles">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>






    <div v-else class="no-data">
      <p>No se encontraron resultados.</p>
    </div>

    <!-- Paginación -->
    <nav v-if="totalPages > 1" class="mt-3">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button class="page-link" @click="currentPage--" :disabled="currentPage === 1">
            Anterior
          </button>
        </li>
        <li class="page-item" v-for="page in totalPages" :key="page" :class="{ active: page === currentPage }">
          <button class="page-link" @click="currentPage = page">
            {{ page }}
          </button>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <button class="page-link" @click="currentPage++" :disabled="currentPage === totalPages">
            Siguiente
          </button>
        </li>
      </ul>
    </nav>

    <!-- Modal Detalles -->
    <div v-if="selectedPersonal" class="modal-overlay">
      <div class="modal-container">
        <PersonalCard :personal="selectedPersonal" @edit-personal="$emit('edit-personal', selectedPersonal)"
          @delete-personal="$emit('delete-personal', selectedPersonal.id)" />
        <button class="btn btn-secondary mt-3" @click="selectedPersonal = null">
          Cancelar
        </button>
      </div>
    </div>

  </div>
</template>


<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import PersonalCard from "./PersonalCard.vue";

export default {
  components: {
    FontAwesomeIcon,
    PersonalCard,
  },
  props: {
    personal: {
      type: Array,
      default: () => [],
    },

  },
  data() {
    return {
      searchQuery: "",
      filterRol: "",
      filterEstado: "",
      currentPage: 1,
      itemsPerPage: 10,
      sortField: "",
      sortOrder: "asc",
      selectedPersonal: null,
    };
  },
  computed: {
    roles() {
      const tipos = this.personal.map(p => p.rol).filter(Boolean);
      const unicos = [...new Set(tipos)];
      return unicos.map(tipo => ({
        tipo,
        descripcion: tipo.charAt(0).toUpperCase() + tipo.slice(1).replaceAll('_', ' ')
      }));
    },
    estados() {
      const tipos = this.personal.map(p => p.estado).filter(Boolean);
      const unicos = [...new Set(tipos)];
      return unicos.map(tipo => ({
        tipo,
        descripcion: tipo.charAt(0).toUpperCase() + tipo.slice(1).replaceAll('_', ' ')
      }));
    },
    filteredPersonal() {
      return this.personal
        .filter((persona) =>
          (
            persona.nombre +
            " " +
            persona.apellido
          )
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase())
        )
        .filter((persona) =>
          this.filterRol ? persona.rol === this.filterRol : true
        )
        .filter((persona) =>
          this.filterEstado ? persona.estado === this.filterEstado : true
        );
    },
    sortedPersonal() {
      return [...this.filteredPersonal].sort((a, b) => {
        if (!this.sortField) return 0;
        const fieldA =
          a[this.sortField]?.toLowerCase?.() || a[this.sortField];
        const fieldB =
          b[this.sortField]?.toLowerCase?.() || b[this.sortField];
        if (fieldA < fieldB) return this.sortOrder === "asc" ? -1 : 1;
        if (fieldA > fieldB) return this.sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    },
    paginatedPersonal() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.sortedPersonal.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredPersonal.length / this.itemsPerPage);
    },
  },
  methods: {
    sort(field) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortField = field;
        this.sortOrder = "asc";
      }
    },
    estadoBadge(estado) {
      const estadoClasses = {
        activo: "badge bg-success",
        inactivo: "badge bg-secondary",
        licencia: "badge bg-info",
        en_entrenamiento: "badge bg-primary",
        de_baja: "badge bg-danger",
        contratista: "badge bg-warning",
        cliente: "badge bg-warning",
      };
      return estadoClasses[estado.toLowerCase()] || "badge bg-light";
    },
    viewPersonalDetails(persona) {
      this.selectedPersonal = persona;
    },
  },
};
</script>

<style scoped>
.list-container {
  margin: 20px;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.list-title {
  font-size: 1.5rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 20px;
}

.table-hover tbody tr:hover {
  background-color: #f1f1f1;
}

.table th.sortable {
  cursor: pointer;
}

.no-data {
  text-align: center;
  font-size: 1.2rem;
  color: #6c757d;
  padding: 20px;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.pagination .page-item.active .page-link {
  background-color: var(--app-primary);
  border-color: var(--app-primary);
}

.pagination .page-link {
  color: var(--app-primary);
}

.pagination .page-link:hover {
  background-color: #e9ecef;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 90%;
}

.card-title {
  font-size: 1.2rem;
  font-weight: bold;
}

.card {
  transition: transform 0.2s ease;
}
.card:hover {
  transform: scale(1.015);
}

</style>